import React, { useState, useEffect, useMemo } from "react"
import { Col, Row, Select, Space, Typography } from "antd"
import PageHeader from "../../../components/Elements/PageHeader"
import { TbPigMoney } from "react-icons/tb"
import { useCollection } from "../../../hooks/useCollection"
import { collection, query, getDocs, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import Overview from "./SalesReport/Overview"
import Chart from "./SalesReport/Chart"
import TopClients from "./SalesReport/TopClients"
import LastMonths from "./SalesReport/LastMonths"

const { Text } = Typography
const { Option } = Select

export default function SalesReport() {
  // Fetch users using your hook
  const { data: userData, loading: usersLoading } = useCollection("users", [
    "group",
    "array-contains",
    "sales",
  ])

  // Selected filters
  const [selectedUser, setSelectedUser] = useState("allUsers")
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1))

  // All invoices fetched once
  const [allInvoices, setAllInvoices] = useState([])
  const [allInvoicesLoading, setAllInvoicesLoading] = useState(false)

  // Warehouse orders
  const [warehouseOrders, setWarehouseOrders] = useState([])
  const [warehouseOrdersLoading, setWarehouseOrdersLoading] = useState(false)

  // Handlers
  const handleSelectUser = (value) => setSelectedUser(value)
  const handleSelectYear = (value) => setSelectedYear(value)
  const handleSelectMonth = (value) => setSelectedMonth(value)

  // Generate year options from 2018 to current year
  const currentYear = new Date().getFullYear()
  const years = []
  for (let y = 2018; y <= currentYear; y++) {
    years.push(y)
  }

  // Month options
  const months = [
    { key: "1", label: "January" },
    { key: "2", label: "February" },
    { key: "3", label: "March" },
    { key: "4", label: "April" },
    { key: "5", label: "May" },
    { key: "6", label: "June" },
    { key: "7", label: "July" },
    { key: "8", label: "August" },
    { key: "9", label: "September" },
    { key: "10", label: "October" },
    { key: "11", label: "November" },
    { key: "12", label: "December" },
  ]

  // Fetch all invoices once
  useEffect(() => {
    async function fetchAllInvoices() {
      setAllInvoicesLoading(true)
      try {
        const q = query(collection(db, "invoices"))
        const querySnapshot = await getDocs(q)
        // Map and filter out invoices with the unwanted userId
        const invoicesData = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((invoice) => invoice.userId !== "E1XNaWvT6PccXRSKB3JNlNehI362")
        setAllInvoices(invoicesData)
      } catch (error) {
        console.error("Error fetching invoices: ", error)
      }
      setAllInvoicesLoading(false)
    }
    fetchAllInvoices()
  }, [])

  // Get warehouse orders and filter out orders with the unwanted uid
  useEffect(() => {
    async function fetchWarehouseOrders() {
      setWarehouseOrdersLoading(true)
      try {
        const constraints = [where("orderStatus", "==", 6)]
        if (selectedUser !== "allUsers") {
          constraints.push(where("uid", "==", selectedUser))
        }
        const q = query(collection(db, "orders"), ...constraints)
        const querySnapshot = await getDocs(q)
        const ordersData = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((order) => order.uid !== "E1XNaWvT6PccXRSKB3JNlNehI362")
        setWarehouseOrders(ordersData)
      } catch (error) {
        console.error("Error fetching warehouse orders: ", error)
      }
      setWarehouseOrdersLoading(false)
    }
    fetchWarehouseOrders()
  }, [selectedUser])

  // Filters for components that need year/month filtering (Overview, TopClients, etc.)
  const yearInvoices = allInvoices.filter((invoice) => {
    if (selectedUser !== "allUsers" && invoice.userId !== selectedUser) return false
    const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
    return invoiceDate.getFullYear() === selectedYear
  })

  const monthInvoices =
    selectedMonth !== "wholeYear"
      ? allInvoices.filter((invoice) => {
          if (selectedUser !== "allUsers" && invoice.userId !== selectedUser) return false
          const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
          return (
            invoiceDate.getFullYear() === selectedYear &&
            invoiceDate.getMonth() === parseInt(selectedMonth, 10) - 1
          )
        })
      : []

  const prevYearInvoices = allInvoices.filter((invoice) => {
    if (selectedUser !== "allUsers" && invoice.userId !== selectedUser) return false
    const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
    return invoiceDate.getFullYear() === selectedYear - 1
  })

  const prevMonthInvoices =
    selectedMonth !== "wholeYear"
      ? allInvoices.filter((invoice) => {
          if (selectedUser !== "allUsers" && invoice.userId !== selectedUser) return false
          const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
          return (
            invoiceDate.getFullYear() === selectedYear - 1 &&
            invoiceDate.getMonth() === parseInt(selectedMonth, 10) - 1
          )
        })
      : []

  // Compute chartInvoices from allInvoices filtered only by user (ignore year/month)
  const chartInvoices = useMemo(() => {
    return allInvoices.filter((invoice) =>
      selectedUser === "allUsers" ? true : invoice.userId === selectedUser
    )
  }, [allInvoices, selectedUser])

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Sales Overview" icon={<TbPigMoney />} />
        </Col>
        <Col span={24}>
          <Space size="large">
            <Space>
              <Text>User:</Text>
              <Select
                style={{ width: 250 }}
                value={selectedUser}
                onChange={handleSelectUser}
                loading={usersLoading}>
                {userData &&
                  userData.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.email}
                    </Option>
                  ))}
                <Option value="allUsers">All Users</Option>
              </Select>
            </Space>
            <Space>
              <Text>Year:</Text>
              <Select style={{ width: 250 }} value={selectedYear} onChange={handleSelectYear}>
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Space>
            <Space>
              <Text>Month:</Text>
              <Select style={{ width: 250 }} value={selectedMonth} onChange={handleSelectMonth}>
                {months.map((month) => (
                  <Option key={month.key} value={month.key}>
                    {month.label}
                  </Option>
                ))}
              </Select>
            </Space>
          </Space>
        </Col>
        <Col span={24}>
          {/* Overview gets the parent's year/month filters */}
          <Overview
            yearInvoices={yearInvoices}
            monthInvoices={monthInvoices}
            prevYearInvoices={prevYearInvoices}
            prevMonthInvoices={prevMonthInvoices}
            loading={allInvoicesLoading}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            months={months}
            warehouseOrders={warehouseOrders}
            warehouseOrdersLoading={warehouseOrdersLoading}
          />
        </Col>
        <Col span={24}>
          {/* Chart gets the chartInvoices (filtered only by user) */}
          <Chart invoices={chartInvoices} loading={allInvoicesLoading} />
        </Col>

        <Col span={8}>
          <TopClients
            invoices={chartInvoices} // or use chartInvoices if desired
            loading={allInvoicesLoading}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          />
        </Col>
        <Col span={8}>
          <TopClients
            invoices={chartInvoices}
            loading={allInvoicesLoading}
            selectedYear={selectedYear}
            mode="yearly"
          />
        </Col>
        <Col span={6}>
          <LastMonths invoices={chartInvoices} selectedManager={selectedUser} />
        </Col>
      </Row>
    </div>
  )
}
